import RankingStatsAggregator from '../calculators/RankingStatsAggregator.js';
import BattingRankingStats from '../enums/BattingRankingStats.js';
import PitchingRankingStats from '../enums/PitchingRankingStats.js';
import RankingScope from '../enums/RankingScope.js';
import RankingScopeContext from '../enums/RankingScopeContext.js';
import RankingScopesFilter from '../filters/RankingScopesFilter.js';
import RankingStat from '../entities/RankingStat.js';

/**
 * This provider can return data for batter's and pitcher's line.
 * Maine source of data are ranking stats
 */
class PlayerLineDataProvider {
    constructor(rankingStatsAggregator, rankingScopesFilter) {
        if (typeof(rankingStatsAggregator) === 'undefined') {
            rankingStatsAggregator = new RankingStatsAggregator();
        }
        else if (!(rankingStatsAggregator instanceof RankingStatsAggregator)) {
            throw new TypeError('rankingStatsAggregator must be an instance inheriting from RankingStatsAggregator class or undefined to use default implementation');
        }

        this.rankingStatsAggregator = rankingStatsAggregator;

        if (typeof(rankingScopesFilter) === 'undefined') {
            rankingScopesFilter = new RankingScopesFilter();
        }
        else if (!(rankingScopesFilter instanceof RankingScopesFilter)) {
            throw new TypeError('rankingScopesFilter must be an instance inheriting from RankingScopesFilter class or undefined to use default implementation');
        }

        this.rankingScopesFilter = rankingScopesFilter;
    }

    buildRankingStatsSummary(rankinParams) {
        return rankinParams.reduce((carry, parameter) => {
            carry[parameter.key] = RankingStat.getDefault(parameter, 0)
            return carry;
        }, {});
    }

    getBattingLine(rankingStats, batterId) {
        this.verifyInput(rankingStats, batterId, false);

        // @see https://flightscope.atlassian.net/browse/BBP-776
        const rankingParams = [
            BattingRankingStats.Hits,
            BattingRankingStats.NumberOfPitches,
            BattingRankingStats.TwoBaseHits,
            BattingRankingStats.ThreeBaseHits,
            BattingRankingStats.HomeRuns,
            BattingRankingStats.Walks,
            BattingRankingStats.Strikeouts
        ];
        const summary = this.buildRankingStatsSummary(rankingParams);

        const filterBy = { context: RankingScopeContext.Batting.key };
        if (batterId) {
            filterBy.tagId = batterId;
        }

        const statsFiltered = this.rankingScopesFilter.filter(
            rankingStats, filterBy
        );
        const statsPerBatter = this.rankingStatsAggregator.aggregate(
            statsFiltered, Object.keys(summary), [RankingScope.tagId.key]
        );
        for (const playerStats of statsPerBatter) {
            this.fillStatsSummary(summary, rankingParams, playerStats);
        }

        return summary;
    }

    getPitchingLine(rankingStats, pitcherId) {
        this.verifyInput(rankingStats, pitcherId, true);

        // @see https://flightscope.atlassian.net/browse/BBP-776
        const rankingParams = [
            PitchingRankingStats.InningsPitched,
            PitchingRankingStats.Strikeouts,
            PitchingRankingStats.WalksAllowed,
            PitchingRankingStats.HitsAllowed,
            PitchingRankingStats.RunsAllowed,
        ];
        const summary = this.buildRankingStatsSummary(rankingParams);
        
        const filterBy = { context: RankingScopeContext.Pitching.key };
        if (pitcherId) {
            filterBy.tagId = pitcherId;
        }

        const statsFiltered = this.rankingScopesFilter.filter(
            rankingStats, filterBy
        );

        const statsPerPitcher = this.rankingStatsAggregator.aggregate(
            statsFiltered, Object.keys(summary), [RankingScope.tagId.key]
        );
        for (const playerStats of statsPerPitcher) {
            this.fillStatsSummary(summary, rankingParams, playerStats);
        }

        return summary;
    }

    verifyInput(rankingStats, playerId, isPlayerIdOptional) {
        if (!(rankingStats instanceof Array)) {
            throw new TypeError('rankingStats must be an array');
        }

        if (!isPlayerIdOptional && typeof(playerId) === 'undefined') {
            throw new TypeError('playerId must be an integer');
        }

        if (typeof(playerId) !== 'undefined' && (!Number.isInteger(playerId) || playerId < 1)) {
            // verify type...
            throw new TypeError('playerId must be an integer greater than 0');
        }
    }

    fillStatsSummary(summary, parameters, playerStats) {
        for (let i = 0; i < parameters.length; i++) {
            const parameter = parameters[i];
            const targetParam = playerStats.parameters.find(p => p.parameter === parameter.key);
            if (targetParam) {
                summary[parameter.key] = targetParam;
            }
        }
    }
}

export default PlayerLineDataProvider;